class CartDrawerController {
  constructor() {
    this.drawer = document.querySelector('[data-cart-drawer]');
    if (!this.drawer) return;

    this.root = window.Shopify?.routes?.root || '/';
    this.panel = this.drawer.querySelector('.cart-drawer__panel');
    this.body = this.drawer.querySelector('[data-cart-drawer-body]');
    this.footer = this.drawer.querySelector('[data-cart-drawer-footer]');
    this.liveRegion = this.drawer.querySelector('[data-cart-drawer-live-region]');
    this.countNodes = [...document.querySelectorAll('[data-cart-count]')];
    this.toggleNodes = [...document.querySelectorAll('[data-cart-toggle]')];
    this.drawerCount = this.drawer.querySelector('[data-cart-drawer-count]');

    this.totalNode = this.drawer.querySelector('[data-cart-drawer-total]');
    this.checkoutTotalNode = this.drawer.querySelector('[data-cart-drawer-checkout-total]');
    this.subtotalNode = this.drawer.querySelector('[data-cart-drawer-subtotal]');
    this.savingsRow = this.drawer.querySelector('[data-cart-drawer-savings-row]');
    this.savingsNode = this.drawer.querySelector('[data-cart-drawer-savings]');
    this.promisesTemplate = this.drawer.querySelector('[data-cart-drawer-promises]');
    this.discountRow = this.drawer.querySelector('[data-cart-drawer-discount-row]');
    this.discountAmountNode = this.drawer.querySelector('[data-cart-drawer-discount-amount]');
    this.discountTagNode = this.drawer.querySelector('[data-cart-drawer-discount-tag]');
    this.discountLabelNode = this.drawer.querySelector('[data-cart-drawer-discount-label]');

    this.tiersRoot = this.drawer.querySelector('[data-cart-drawer-tiers]');
    this.tiersCopy = this.drawer.querySelector('[data-cart-drawer-tiers-copy]');
    this.tiersFill = this.drawer.querySelector('[data-cart-drawer-tiers-fill]');
    this.tiersMarkers = this.drawer.querySelector('[data-cart-drawer-tiers-markers]');
    this.tiers = (this.parseJsonNode('[data-cart-drawer-qty-tiers]') || [])
      .map((tier) => ({ qty: Number(tier.qty), percent: Number(tier.percent) }))
      .filter((tier) => tier.qty > 0 && tier.percent > 0)
      .sort((a, b) => a.qty - b.qty);

    this.currency = this.drawer.dataset.cartCurrency || 'EUR';
    this.showCompareAt = this.drawer.dataset.cartDrawerShowCompareAt === 'true';
    this.variantCompareAtPrices = this.parseVariantCompareAtPrices();

    this.activeAddButton = null;
    this.activeAddLabel = null;
    this.activeAddLabelText = '';
    this.lastActiveElement = null;
    this.pendingItemKeys = new Set();
    this.isRefreshing = false;

    this.emptyTitle = this.drawer.dataset.cartDrawerEmptyTitle || 'Your cart is empty';
    this.emptyCopy = this.drawer.dataset.cartDrawerEmptyCopy || 'Add something you love and it will appear here.';
    this.decreaseQuantityLabel = this.drawer.dataset.cartDrawerDecreaseLabel || 'Decrease quantity';
    this.increaseQuantityLabel = this.drawer.dataset.cartDrawerIncreaseLabel || 'Increase quantity';
    this.removeLabel = this.drawer.dataset.cartDrawerRemoveLabel || 'Remove';
    this.youSaveLabel = this.drawer.dataset.cartDrawerYouSaveLabel || 'You save';
    this.liveRegionTemplate =
      this.drawer.dataset.cartDrawerLiveTemplate || 'Cart updated. [[count]] items. Total [[subtotal]].';
    this.addingLabel = this.drawer.dataset.cartDrawerAddingLabel || 'Adding...';

    const promisesSlot = this.drawer.querySelector('[data-cart-drawer-promises-slot]');
    if (promisesSlot && this.promisesTemplate) promisesSlot.innerHTML = this.promisesTemplate.innerHTML;

    this.bindUI();
  }

  bindUI() {
    this.toggleNodes.forEach((trigger) => {
      trigger.setAttribute('aria-haspopup', 'dialog');
      trigger.setAttribute('aria-expanded', 'false');
      trigger.addEventListener('click', async (event) => {
        event.preventDefault();
        this.lastActiveElement = trigger;
        await this.refresh();
        this.open();
      });
    });

    this.drawer.querySelectorAll('[data-cart-drawer-close]').forEach((button) => {
      button.addEventListener('click', () => this.close());
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && this.drawer.classList.contains('is-open')) {
        this.close();
      } else if (event.key === 'Tab' && this.drawer.classList.contains('is-open')) {
        this.trapFocus(event);
      }
    });

    document.addEventListener('submit', (event) => this.handleAddToCart(event));
    this.drawer.addEventListener('click', (event) => this.handleDrawerClick(event));
  }

  async handleAddToCart(event) {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    if (!form.matches('[data-product-form]')) return;

    const submitter = event.submitter;
    if (!submitter || submitter.getAttribute('name') !== 'add') return;

    event.preventDefault();

    const formData = new FormData(form);

    // Neem het aantal uit het veld dat het dichtst bij de geklikte knop staat
    // (werkt voor zowel de gewone knop als de sticky balk)
    let scope = submitter.parentElement;
    let qtyInput = null;
    while (scope && scope !== document.body) {
      qtyInput = scope.querySelector('input[name="quantity"], input[type="number"]');
      if (qtyInput) break;
      scope = scope.parentElement;
    }
    if (qtyInput && Number(qtyInput.value) > 0) {
      formData.set('quantity', qtyInput.value);
    }

    this.activeAddButton = submitter;
    this.activeAddLabel = submitter.querySelector('[data-add-to-cart-label]');
    this.activeAddLabelText = this.activeAddLabel?.textContent || '';
    this.cacheFormVariantCompareAt(form);

    try {
      this.setAddButtonState(true);

      const response = await fetch(`${this.root}cart/add.js`, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: formData
      });

      if (!response.ok) throw new Error('Unable to add item to cart');

      await response.json();
      await this.refresh();
      this.open();
    } catch (error) {
      console.error(error);
      window.location.href = form.action || '/cart';
    } finally {
      this.setAddButtonState(false);
    }
  }

  async handleDrawerClick(event) {
    const quantityTrigger = event.target.closest('[data-cart-quantity-change]');
    if (quantityTrigger) {
      const item = quantityTrigger.closest('[data-cart-item-key]');
      if (!item) return;

      const key = item.dataset.cartItemKey;
      if (!key || this.pendingItemKeys.has(key)) return;

      const delta = Number(quantityTrigger.dataset.cartQuantityDelta || 0);
      const quantityValue = item.querySelector('.cart-drawer__quantity-value');
      const currentQuantity = Number(quantityValue?.textContent || 0);
      await this.changeItem(key, Math.max(0, currentQuantity + delta));
      return;
    }

    const removeTrigger = event.target.closest('[data-cart-remove]');
    if (removeTrigger) {
      const item = removeTrigger.closest('[data-cart-item-key]');
      if (!item) return;

      const key = item.dataset.cartItemKey;
      if (!key || this.pendingItemKeys.has(key)) return;

      await this.changeItem(key, 0);
    }
  }

  async changeItem(key, quantity) {
    if (!key || this.pendingItemKeys.has(key)) return;

    this.pendingItemKeys.add(key);
    this.setItemPendingState(key, true);

    try {
      const response = await fetch(`${this.root}cart/change.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: key, quantity })
      });

      if (!response.ok) throw new Error('Unable to update cart item');

      const cart = await response.json();
      await this.render(cart);
    } catch (error) {
      console.error(error);
    } finally {
      this.pendingItemKeys.delete(key);
      this.setItemPendingState(key, false);
    }
  }

  async refresh() {
    if (this.isRefreshing) return null;

    this.isRefreshing = true;
    this.panel?.setAttribute('aria-busy', 'true');

    try {
      const response = await fetch(`${this.root}cart.js`, { headers: { Accept: 'application/json' } });
      const cart = await response.json();
      await this.render(cart);
      return cart;
    } finally {
      this.isRefreshing = false;
      this.panel?.setAttribute('aria-busy', 'false');
    }
  }

  async render(cart) {
    await this.ensureCompareAtPrices(cart);
    this.hydrateCompareAtPrices(cart);

    const itemCount = cart.item_count || 0;
    const total = cart.total_price || 0;

    if (this.drawerCount) this.drawerCount.textContent = itemCount;

    this.countNodes.forEach((node) => {
      node.textContent = itemCount;
      node.hidden = itemCount === 0;
    });

    if (this.totalNode) this.totalNode.textContent = this.formatMoney(total);
    if (this.checkoutTotalNode) this.checkoutTotalNode.textContent = this.formatMoney(total);

    if (this.liveRegion) {
      this.liveRegion.textContent = this.liveRegionTemplate
        .replace('[[count]]', String(itemCount))
        .replace('[[subtotal]]', this.formatMoney(total));
    }

    if (this.footer) this.footer.hidden = itemCount === 0;

    this.renderSummary(cart);
    this.renderDiscount(cart);
    this.renderTiers(itemCount);

    if (!itemCount) {
      this.body.innerHTML = `
        <div class="cart-drawer__empty" data-cart-drawer-empty>
          <p class="cart-drawer__empty-title">${this.escapeHtml(this.emptyTitle)}</p>
          <p class="cart-drawer__empty-copy">${this.escapeHtml(this.emptyCopy)}</p>
        </div>
      `;
      return;
    }

    const itemsMarkup = cart.items.map((item) => this.itemMarkup(item)).join('');
    const promisesMarkup = this.promisesTemplate ? this.promisesTemplate.innerHTML : '';
    this.body.innerHTML = `<ul class="cart-drawer__items" data-cart-drawer-items role="list">${itemsMarkup}</ul>${promisesMarkup}`;
  }

  // Subtotal = old prices, Savings = sale savings, Bundle discount = tier discount (renderDiscount).
  renderSummary(cart) {
    const items = cart.items || [];
    const compareTotal = items.reduce(
      (sum, item) => sum + Math.max(Number(item.compare_at_line_price || 0), Number(item.original_line_price || 0)),
      0
    );
    const originalTotal = items.reduce((sum, item) => sum + Number(item.original_line_price || 0), 0);
    const saleSavings = compareTotal - originalTotal;

    if (this.subtotalNode) this.subtotalNode.textContent = this.formatMoney(compareTotal);
    if (this.savingsRow) this.savingsRow.hidden = saleSavings <= 0;
    if (this.savingsNode) this.savingsNode.textContent = `-${this.formatMoney(saleSavings)}`;
  }

  itemMarkup(item) {
    const finalLine = Number(item.final_line_price || 0);
    const baseLine = Math.max(Number(item.compare_at_line_price || 0), Number(item.original_line_price || 0));
    const saved = baseLine - finalLine;
    const isPending = this.pendingItemKeys.has(item.key);

    const imageMarkup = item.image
      ? `<a href="${item.url}" class="cart-drawer__item-media">
          <img src="${item.image}" alt="${this.escapeHtml(item.product_title)}" class="cart-drawer__item-image" loading="lazy">
        </a>`
      : `<div class="cart-drawer__item-media"></div>`;

    const variantMarkup =
      item.variant_title && item.variant_title !== 'Default Title'
        ? `<p class="cart-drawer__item-variant">${this.escapeHtml(item.variant_title)}</p>`
        : '';

    const compareMarkup =
      this.showCompareAt && baseLine > finalLine
        ? `<p class="cart-drawer__item-compare-price">${this.formatMoney(baseLine)}</p>`
        : '';

    const saveMarkup =
      saved > 0
        ? `<p class="cart-drawer__save">${this.escapeHtml(this.youSaveLabel)} ${this.formatMoney(saved)}</p>`
        : '';

    return `
      <li class="cart-drawer__item${isPending ? ' is-pending' : ''}" data-cart-item-key="${item.key}" aria-busy="${isPending ? 'true' : 'false'}">
        ${imageMarkup}
        <div class="cart-drawer__item-content">
          <div class="cart-drawer__item-top">
            <div>
              <a href="${item.url}" class="cart-drawer__item-title">${this.escapeHtml(item.product_title)}</a>
              ${variantMarkup}
            </div>
            <div class="cart-drawer__item-prices">
              ${compareMarkup}
              <p class="cart-drawer__item-price">${this.formatMoney(finalLine)}</p>
              ${saveMarkup}
            </div>
          </div>
          <div class="cart-drawer__item-bottom">
            <div class="cart-drawer__quantity">
              <button type="button" class="cart-drawer__quantity-button" data-cart-quantity-change data-cart-quantity-delta="-1" aria-label="${this.escapeHtml(this.decreaseQuantityLabel)}" ${isPending ? 'disabled' : ''}>-</button>
              <span class="cart-drawer__quantity-value">${item.quantity}</span>
              <button type="button" class="cart-drawer__quantity-button" data-cart-quantity-change data-cart-quantity-delta="1" aria-label="${this.escapeHtml(this.increaseQuantityLabel)}" ${isPending ? 'disabled' : ''}>+</button>
            </div>
            <button type="button" class="cart-drawer__remove" data-cart-remove aria-label="${this.escapeHtml(this.removeLabel)}" ${isPending ? 'disabled' : ''}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M3 6h18M8 6V4h8v2M6 6l1 14h10l1-14"/></svg>
            </button>
          </div>
        </div>
      </li>
    `;
  }

  // Shows the discount Shopify actually applied (from your automatic discounts).
  renderDiscount(cart) {
    if (!this.discountRow) return;

    const amount = Number(cart.total_discount || 0);
    this.discountRow.hidden = amount <= 0;
    if (amount <= 0) return;

    if (this.discountAmountNode) this.discountAmountNode.textContent = `-${this.formatMoney(amount)}`;

    const application = this.findDiscountApplication(cart);
    let label = '';
    if (application) {
      label = application.value_type === 'percentage' ? `${Number(application.value)}%` : application.title || '';
    }

    if (this.discountLabelNode) this.discountLabelNode.textContent = label;
    if (this.discountTagNode) this.discountTagNode.hidden = !label;
  }

  findDiscountApplication(cart) {
    for (const item of cart.items || []) {
      const allocation = (item.line_level_discount_allocations || [])[0];
      if (allocation?.discount_application) return allocation.discount_application;
    }
    return (cart.cart_level_discount_applications || [])[0] || null;
  }

  // Progress bar based on number of items in the cart.
  renderTiers(count) {
    if (!this.tiersRoot) return;

    const tiers = this.tiers;
    const show = tiers.length > 0 && count > 0;
    this.tiersRoot.hidden = !show;
    if (!show) return;

    const n = tiers.length;
    const position = (index) => ((index + 1) / n) * 100;
    const nextIndex = tiers.findIndex((tier) => count < tier.qty);

    let progress = 100;
    if (nextIndex !== -1) {
      const prevQty = nextIndex === 0 ? 0 : tiers[nextIndex - 1].qty;
      const prevPos = nextIndex === 0 ? 0 : position(nextIndex - 1);
      const span = tiers[nextIndex].qty - prevQty;
      progress = prevPos + ((count - prevQty) / span) * (position(nextIndex) - prevPos);
    }
    if (this.tiersFill) this.tiersFill.style.width = `${progress}%`;

    const current = nextIndex === -1 ? tiers[n - 1] : nextIndex > 0 ? tiers[nextIndex - 1] : null;
    const next = nextIndex === -1 ? null : tiers[nextIndex];

    let copy = '';
    if (current) copy += `You've unlocked <strong>${current.percent}% off</strong>! `;
    if (next) {
      const left = next.qty - count;
      copy += `Add <strong>${left}</strong> ${left === 1 ? 'item' : 'items'} to unlock <strong>${next.percent}% off</strong>!`;
    }
    if (this.tiersCopy) this.tiersCopy.innerHTML = copy.trim();

    if (this.tiersMarkers) {
      this.tiersMarkers.innerHTML = tiers
        .map((tier, index) => {
          const done = count >= tier.qty;
          const left = position(index);
          return `
            <span class="cdt__marker${done ? ' is-done' : ''}" style="left:${left}%"></span>
            <span class="cdt__label${done ? ' is-done' : ''}${index === n - 1 ? ' is-last' : ''}" style="left:${left}%">${tier.percent}% off</span>
          `;
        })
        .join('');
    }
  }

    open() {
    if (!this.drawer.classList.contains('is-open')) {
      this.scrollPosition = window.scrollY;
      document.body.style.top = `-${this.scrollPosition}px`;
      document.documentElement.classList.add('cart-drawer-open');
      document.body.classList.add('cart-drawer-open');
    }

    this.drawer.classList.add('is-open');
    this.drawer.setAttribute('aria-hidden', 'false');
    this.toggleNodes.forEach((node) => node.setAttribute('aria-expanded', 'true'));
    this.panel
      .querySelector('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])')
      ?.focus({ preventScroll: true });
  }

  close() {
    if (!this.drawer.classList.contains('is-open')) return;

    this.drawer.classList.remove('is-open');
    this.drawer.setAttribute('aria-hidden', 'true');
    this.unlockScroll();
    this.toggleNodes.forEach((node) => node.setAttribute('aria-expanded', 'false'));
    this.lastActiveElement?.focus({ preventScroll: true });
  }

  unlockScroll() {
    document.documentElement.classList.remove('cart-drawer-open');
    document.body.classList.remove('cart-drawer-open');
    document.body.style.top = '';
    window.scrollTo({ top: this.scrollPosition || 0, behavior: 'instant' });
  }

  setAddButtonState(isLoading) {
    if (!this.activeAddButton) return;

    this.activeAddButton.disabled = isLoading;
    if (this.activeAddLabel) {
      this.activeAddLabel.textContent = isLoading ? this.addingLabel : this.activeAddLabelText;
    }
  }

  setItemPendingState(key, isPending) {
    const item = this.body?.querySelector(`[data-cart-item-key="${CSS.escape(key)}"]`);
    if (!item) return;

    item.classList.toggle('is-pending', isPending);
    item.setAttribute('aria-busy', isPending ? 'true' : 'false');
    item.querySelectorAll('button').forEach((button) => {
      button.disabled = isPending;
    });
  }

  trapFocus(event) {
    const focusableNodes = [
      ...this.panel.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])')
    ].filter((node) => !node.hasAttribute('disabled'));
    if (!focusableNodes.length) return;

    const firstNode = focusableNodes[0];
    const lastNode = focusableNodes[focusableNodes.length - 1];

    if (event.shiftKey && document.activeElement === firstNode) {
      event.preventDefault();
      lastNode.focus();
    } else if (!event.shiftKey && document.activeElement === lastNode) {
      event.preventDefault();
      firstNode.focus();
    }
  }

  formatMoney(cents) {
    return new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
      style: 'currency',
      currency: this.currency
    }).format((cents || 0) / 100);
  }

  escapeHtml(value) {
    return String(value || '')
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#39;');
  }

  parseJsonNode(selector) {
    const node = this.drawer.querySelector(selector);
    if (!node) return null;

    try {
      return JSON.parse(node.textContent || 'null');
    } catch (error) {
      console.error(error);
      return null;
    }
  }

  parseVariantCompareAtPrices() {
    const value = this.parseJsonNode('[data-cart-drawer-compare-at-prices]');
    const prices = new Map();

    Object.entries(value || {}).forEach(([variantId, compareAtPrice]) => {
      const id = Number(variantId);
      if (id) prices.set(id, Number(compareAtPrice || 0));
    });

    return prices;
  }

  cacheFormVariantCompareAt(form) {
    const variantId = Number(form.querySelector('[name="id"]')?.value || 0);
    if (!variantId || this.variantCompareAtPrices.has(variantId)) return;

    const productJsonNode = form.closest('[data-product-section]')?.querySelector('[data-product-json]');
    if (!productJsonNode) return;

    try {
      const product = JSON.parse(productJsonNode.textContent || '{}');
      const variant = (product.variants || []).find((item) => Number(item.id) === variantId);
      if (variant) this.variantCompareAtPrices.set(variantId, Number(variant.compare_at_price || 0));
    } catch (error) {
      console.error(error);
    }
  }

  // Fetches compare-at prices for items we don't know yet (e.g. added from a collection page).
  async ensureCompareAtPrices(cart) {
    const missingItems = (cart.items || []).filter(
      (item) => !this.variantCompareAtPrices.has(Number(item.variant_id))
    );
    const handles = [...new Set(missingItems.map((item) => item.handle).filter(Boolean))];
    if (!handles.length) return;

    await Promise.all(
      handles.map(async (handle) => {
        try {
          const response = await fetch(`${this.root}products/${handle}.js`, { headers: { Accept: 'application/json' } });
          if (!response.ok) return;
          const product = await response.json();
          (product.variants || []).forEach((variant) => {
            this.variantCompareAtPrices.set(Number(variant.id), Number(variant.compare_at_price || 0));
          });
        } catch (error) {
          console.error(error);
        }
      })
    );

    missingItems.forEach((item) => {
      const id = Number(item.variant_id);
      if (!this.variantCompareAtPrices.has(id)) this.variantCompareAtPrices.set(id, 0);
    });
  }

  hydrateCompareAtPrices(cart) {
    (cart.items || []).forEach((item) => {
      const compareAtUnit = Number(this.variantCompareAtPrices.get(Number(item.variant_id)) || 0);
      const originalUnit = Number(item.original_price || 0);

      item.compare_at_line_price = compareAtUnit > originalUnit ? compareAtUnit * Number(item.quantity || 0) : 0;
    });
  }
}

document.addEventListener('DOMContentLoaded', () => {
  new CartDrawerController();
});